import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./footer";
import { toast } from "react-toastify";

const StrategyRebalancingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const token = window.sessionStorage.getItem("jwtToken");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultTitle, setResultTitle] = useState("");
  const [resultMessage, setResultMessage] = useState("");
  const [resultType, setResultType] = useState("");
  const [errorPopup, setErrorPopup] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [stocks, setStocks] = useState(location.state?.rebalanceData || []);
  const strategyName = location.state?.strategyName || "";
  const businessDate = location.state?.rebalanceDate || "";

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [showAddModal, setShowAddModal] = useState(false);

  const formatDateForInput = (value) => value;

  const formatDateForRequest = (value) => value;

  const sortedStocks = [...stocks].sort((a, b) => {
    const dateA = a.entryDate
      ? new Date(formatDateForInput(a.entryDate))
      : new Date(0);

    const dateB = b.entryDate
      ? new Date(formatDateForInput(b.entryDate))
      : new Date(0);

    return dateB - dateA; // DESC
  });

  const totalPages = Math.ceil(sortedStocks.length / itemsPerPage);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;

  const paginatedStocks = sortedStocks.slice(indexOfFirst, indexOfLast);

  const toNumber = (value, fallback = 0) => {
    if (value === "" || value === null || value === undefined) return fallback;
    return Number(value);
  };

  const buildStockPayload = (stock) => ({
    ...stock,
    entryDate: formatDateForRequest(stock.entryDate),
    exitDate: formatDateForRequest(stock.exitDate),
    entryPrice: toNumber(stock.entryPrice),
    exitPrice: toNumber(stock.exitPrice),
    quantity: toNumber(stock.quantity),
    weightage: toNumber(stock.weightage),
    status: toNumber(stock.status, 1),
    currentPrice: toNumber(stock.currentPrice),
    pnl: toNumber(stock.pnl),
    averageEntryPrice: toNumber(stock.averageEntryPrice),
  });

  const validateStock = (stock) => {
    const entry = formatDateForInput(stock.entryDate);
    const exit = formatDateForInput(stock.exitDate);

    if (entry && exit && new Date(exit) < new Date(entry)) {
      return "Exit Date cannot be before Entry Date";
    }

    return "";
  };

  const [addFormData, setAddFormData] = useState({
    strategyCode: strategyName?.code || "",
    strategyName: strategyName?.title || "",
    stockName: "",
    businessDate: businessDate,
    entryDate: "",
    entryPrice: "",
    exitDate: "",
    exitPrice: "",
    quantity: "",
    weightage: "",
    status: 1,
    currentPrice: "",
    pnl: "",
    averageEntryPrice: "",
  });

  const handleFieldChange = (id, field, value) => {
    setStocks((prevStocks) =>
      prevStocks.map((stock) =>
        stock.id === id ? { ...stock, [field]: value } : stock,
      ),
    );
  };

  const handleUpdateStock = (stock) => {
    const validationMessage = validateStock(stock);

  if (validationMessage) {
    toast.error(validationMessage);
    return;
  }

  setEditingId(null);
  toast.success("Stock updated successfully");
  };

  const handleSaveRebalance = async () => {
    setShowConfirmModal(false);

    const invalidStock = stocks.find((stock) => validateStock(stock));
    if (invalidStock) {
      setErrorPopup(validateStock(invalidStock));
      return;
    }

    try {
      const payload = stocks.map((stock) => ({
        ...buildStockPayload(stock),
        strategyName: strategyName.title,
        businessDate,
      }));

      const response = await fetch(
        `${BASE_URL}/strategy-selected-stocks/save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save rebalancing data");
      }

      setResultTitle("Success");
      setResultMessage("Rebalance saved successfully.");
      setResultType("success");
      setShowResultModal(true);

      setTimeout(() => {
        navigate("/strategyRebalanceManager");
      }, 1500);
    } catch (error) {
      setResultTitle("Error");
      setResultMessage(error.message || "Something went wrong.");
      setResultType("danger");
      setShowResultModal(true);
    }
  };

  const handleAddStock = () => {
    const errors = {};

    const {
      stockName,
      entryDate,
      entryPrice,
      exitDate,
      exitPrice,
      quantity,
      weightage,
      currentPrice,
      pnl,
      averageEntryPrice,
    } = addFormData;

    if (!stockName?.trim()) errors.stockName = "Stock Name is required";
    if (!entryPrice) errors.entryPrice = "Entry Price is required";
    if (!exitPrice) errors.exitPrice = "Exit Price is required";
    if (!quantity) errors.quantity = "Quantity is required";
    if (!weightage) errors.weightage = "Weightage is required";
    if (!currentPrice) errors.currentPrice = "Current Price is required";
    if (!pnl) errors.pnl = "PnL is required";
    if (!averageEntryPrice)
      errors.averageEntryPrice = "Avg Entry Price is required";

    const entry = formatDateForInput(entryDate);
    const exit = formatDateForInput(exitDate);

    if (entry && exit && new Date(exit) < new Date(entry)) {
      errors.exitDate = "Exit Date cannot be before Entry Date";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const newStock = buildStockPayload({
      id: Date.now(),
      strategyCode: addFormData.strategyCode,
      strategyName: addFormData.strategyName,
      stockName: stockName.trim(),
      businessDate: businessDate,
      entryDate: addFormData.entryDate,
      entryPrice: addFormData.entryPrice,
      exitDate: addFormData.exitDate,
      exitPrice: addFormData.exitPrice,
      quantity: addFormData.quantity,
      weightage: addFormData.weightage,
      status: addFormData.status,
      currentPrice: addFormData.currentPrice,
      pnl: addFormData.pnl,
      averageEntryPrice: addFormData.averageEntryPrice,
    });

    setStocks((prev) => [...prev, newStock]);

    toast.success("Stock Added Successfully");

    setShowAddModal(false);
  };

  return (
    <>
      <style>
        {`
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          input[type="number"] {
            appearance: textfield;
            -moz-appearance: textfield;
          }
          `}
      </style>
      <Navbar />

      <div className="main-content">
        <div className="container-fluid">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ marginTop: "10px", marginBottom: "10px" }}
          >
            <button
              className="btn btn-primary"
              onClick={() => navigate("/strategyRebalanceManager")}
            >
              Back
            </button>

            <h4>Strategy Rebalancing - {strategyName?.title}</h4>

            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setAddFormData({
                    strategyCode: strategyName?.code || "",
                    strategyName: strategyName?.title || "",
                    stockName: "",
                    businessDate: businessDate,
                    entryDate: "",
                    entryPrice: "",
                    exitDate: "",
                    exitPrice: "",
                    quantity: "",
                    weightage: "",
                    status: 1,
                    currentPrice: "",
                    pnl: "",
                    averageEntryPrice: "",
                  });

                  setFormErrors({});
                  setShowAddModal(true);
                }}
              >
                + Add Stock
              </button>

              <button
                className="btn btn-success"
                onClick={() => setShowConfirmModal(true)}
              >
                Save Rebalance
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <table className="table table-striped table-borderless">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Strategy Code</th>
                    <th>Stock Name</th>
                    <th>Business Date</th>
                    <th>Entry Date</th>
                    <th>Entry Price</th>
                    <th>Exit Date</th>
                    <th>Exit Price</th>
                    <th>Quantity</th>
                    <th>Weightage</th>
                    <th>Status</th>
                    <th>Current Price</th>
                    <th>PnL</th>

                    <th>Avg Entry</th>

                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedStocks.map((stock, index) => (
                    <tr key={stock.id}>
                      <td>{stock.id}</td>
                      <td>{stock.strategyCode}</td>
                      <td>{stock.stockName}</td>
                      <td>{businessDate}</td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={formatDateForInput(stock.entryDate)}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "entryDate",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.entryDate
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.entryPrice || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "entryPrice",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.entryPrice
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            min={formatDateForInput(stock.entryDate)}
                            value={formatDateForInput(stock.exitDate)}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "exitDate",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.exitDate
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.exitPrice || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "exitPrice",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.exitPrice
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.quantity || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "quantity",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.quantity
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.weightage || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "weightage",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.weightage
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.status || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "status",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.status
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.currentPrice || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "currentPrice",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.currentPrice
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.pnl || ""}
                            onChange={(e) =>
                              handleFieldChange(stock.id, "pnl", e.target.value)
                            }
                          />
                        ) : (
                          stock.pnl
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={stock.averageEntryPrice || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                stock.id,
                                "averageEntryPrice",
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          stock.averageEntryPrice
                        )}
                      </td>
                      <td>
                        {editingId === stock.id ? (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleUpdateStock(stock)}
                          >
                            Save
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setEditingId(stock.id)}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <nav className="d-flex justify-content-center mt-3">
                <ul className="pagination pagination-sm">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>

                  {[...Array(totalPages)].map((_, i) => (
                    <li
                      key={i}
                      className={`page-item ${
                        currentPage === i + 1 ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}

                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
      {showConfirmModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Save</h5>
                </div>

                <div className="modal-body">
                  <p>Are you sure you want to save the rebalancing changes?</p>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={handleSaveRebalance}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {showResultModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className={`modal-header bg-${resultType} text-white`}>
                  <h5 className="modal-title">{resultTitle}</h5>
                </div>

                <div className="modal-body">
                  <p>{resultMessage}</p>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowResultModal(false);

                      if (resultType === "success") {
                        navigate("/strategyRebalancingPage");
                      }
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showAddModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Add Stock</h5>

                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowAddModal(false);
                      setFormErrors({});
                    }}
                  />
                </div>

                <div className="modal-body">
                  <label>Stock Name</label>
                  <input
                    className="form-control mb-1"
                    value={addFormData.stockName}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        stockName: e.target.value,
                      });
                      setFormErrors((prev) => ({ ...prev, stockName: "" }));
                    }}
                  />
                  {formErrors.stockName && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.stockName}
                    </div>
                  )}

                  <label>Entry Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formatDateForInput(addFormData.entryDate)}
                    onChange={(e) => {
                      setAddFormData((prev) => ({
                        ...prev,
                        entryDate: e.target.value,
                      }));
                      setFormErrors((prev) => ({ ...prev, entryDate: "" }));
                    }}
                  />

                  <label>Entry Price</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.entryPrice}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        entryPrice: e.target.value,
                      });
                      setFormErrors((prev) => ({ ...prev, entryPrice: "" }));
                    }}
                  />
                  {formErrors.entryPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.entryPrice}
                    </div>
                  )}

                  <label>Exit Date</label>
                  <input
                    type="date"
                    className="form-control"
                    min={formatDateForInput(addFormData.entryDate)}
                    value={formatDateForInput(addFormData.exitDate)}
                    onChange={(e) => {
                      setAddFormData((prev) => ({
                        ...prev,
                        exitDate: e.target.value,
                      }));
                      setFormErrors((prev) => ({ ...prev, exitDate: "" }));
                    }}
                  />

                  <label>Exit Price</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.exitPrice}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        exitPrice: e.target.value,
                      })
                    }
                  />

                  {formErrors.exitPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.exitPrice}
                    </div>
                  )}

                  <label>Quantity</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.quantity}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        quantity: e.target.value,
                      });
                      setFormErrors((prev) => ({ ...prev, quantity: "" }));
                    }}
                  />
                  {formErrors.quantity && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.quantity}
                    </div>
                  )}

                  <label>Weightage</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.weightage}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        weightage: e.target.value,
                      });
                      setFormErrors((prev) => ({ ...prev, weightage: "" }));
                    }}
                  />
                  {formErrors.weightage && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.weightage}
                    </div>
                  )}

                  <label>Status</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.status}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        status: e.target.value,
                      })
                    }
                  />

                  <label>Current Price</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.currentPrice}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        currentPrice: e.target.value,
                      });
                      setFormErrors((prev) => ({ ...prev, currentPrice: "" }));
                    }}
                  />
                  {formErrors.currentPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.currentPrice}
                    </div>
                  )}

                  <label>PnL</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.pnl}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        pnl: e.target.value,
                      });
                      setFormErrors((prev) => ({ ...prev, pnl: "" }));
                    }}
                  />
                  {formErrors.pnl && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.pnl}
                    </div>
                  )}

                  <label>Avg Entry</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.averageEntryPrice}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        averageEntryPrice: e.target.value,
                      });
                      setFormErrors((prev) => ({
                        ...prev,
                        averageEntryPrice: "",
                      }));
                    }}
                  />
                  {formErrors.averageEntryPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.averageEntryPrice}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddModal(false);
                      setFormErrors({});
                    }}
                  >
                    Cancel
                  </button>

                  <button className="btn btn-success" onClick={handleAddStock}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {errorPopup && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Validation Error</h5>
                </div>

                <div className="modal-body">{errorPopup}</div>

                <div className="modal-footer">
                  <button
                    className="btn btn-danger"
                    onClick={() => setErrorPopup("")}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
      <Footer />
    </>
  );
};

export default StrategyRebalancingPage;
